const NUMBER_CANDIDATES = [
  'edition_number',
  'editionNumber',
  'limitedEditionNumber',
  'limited_edition_number',
  'viewer_edition_number',
  'viewerEditionNumber',
  'copy_number',
  'copyNumber',
  'edition.number',
  'limitedEdition.number',
];

const SIZE_CANDIDATES = [
  'edition_size',
  'editionSize',
  'limitedEditionSize',
  'limited_edition_size',
  'edition.size',
  'limitedEdition.size',
];

const SIZE_KEY_CANDIDATES = [
  'size',
  'size_key',
  'sizeKey',
  'limitedEditionSize',
  'limitedEdition.size',
  'limitedEdition.size_key',
  'limitedEdition.sizeKey',
  'selected_size',
  'selectedSize',
  'selection.size',
  'size_data.size',
  'size_data.key',
  'limitedEdition.size_data.size',
  'limitedEdition.size_data.key',
];

const SIZE_DATA_CANDIDATES = [
  'size_data',
  'sizeData',
  'limitedEdition.size_data',
  'limitedEdition.sizeData',
  'selected_size_data',
  'selectedSizeData',
  'limitedEdition.selected_size_data',
  'limitedEdition.selectedSizeData',
];

function readNestedValue(object, path) {
  if (!object || typeof object !== 'object') {
    return null;
  }

  if (!path.includes('.')) {
    return object[path];
  }

  return path.split('.').reduce((accumulator, segment) => {
    if (accumulator && typeof accumulator === 'object' && segment in accumulator) {
      return accumulator[segment];
    }
    return null;
  }, object);
}

function pickFirstCandidate(metadata, candidates) {
  for (let index = 0; index < candidates.length; index += 1) {
    const value = readNestedValue(metadata, candidates[index]);
    if (value !== undefined && value !== null && `${value}`.toString().trim().length > 0) {
      return value;
    }
  }

  return null;
}

export function resolveEditionNumber(metadata = {}) {
  return pickFirstCandidate(metadata, NUMBER_CANDIDATES);
}

export function resolveEditionSize(metadata = {}) {
  return pickFirstCandidate(metadata, SIZE_CANDIDATES);
}

function pickFirstCandidateFromSources(sources = [], candidates = []) {
  for (let index = 0; index < sources.length; index += 1) {
    const source = sources[index];
    if (!source || typeof source !== 'object') {
      continue;
    }
    const value = pickFirstCandidate(source, candidates);
    if (value !== undefined && value !== null && `${value}`.toString().trim().length > 0) {
      return value;
    }
  }
  return null;
}

function isPositiveInteger(value) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0;
}

function mergeLimitedEditionSections(...sources) {
  const merged = {};

  const assignSection = (section) => {
    if (!section || typeof section !== 'object') {
      return;
    }

    Object.entries(section).forEach(([key, value]) => {
      if (value !== undefined) {
        merged[key] = value;
      }
    });

    const fallbackNumber =
      section.number
      ?? section.edition_number
      ?? section.copy_number
      ?? section.copyNumber
      ?? section.viewer_edition_number
      ?? section.viewerEditionNumber
      ?? null;

    if (fallbackNumber !== null) {
      merged.number = fallbackNumber;
    }

    const fallbackSize =
      section.size
      ?? section.size_key
      ?? section.sizeKey
      ?? section.key
      ?? section.name
      ?? section.label
      ?? null;

    if (fallbackSize !== null) {
      merged.size = fallbackSize;
    }

    const fallbackEditionSize =
      section.edition_size
      ?? section.editionSize
      ?? section.quantity
      ?? section.total
      ?? section.limit
      ?? section.run
      ?? null;

    if (fallbackEditionSize !== null) {
      merged.edition_size = fallbackEditionSize;
    }
  };

  for (let index = 0; index < sources.length; index += 1) {
    const source = sources[index];
    if (!source || typeof source !== 'object') {
      continue;
    }

    if (source.limitedEdition && typeof source.limitedEdition === 'object') {
      assignSection(source.limitedEdition);
    }

    if (source.limited_edition && typeof source.limited_edition === 'object') {
      assignSection(source.limited_edition);
    }
  }

  return merged;
}

export function buildLimitedEditionMetadata(artworkData = {}) {
  const productMetadataSource = artworkData?.product_metadata ?? {};
  const metadataSource = artworkData?.metadata ?? {};

  const productMetadata = {
    ...metadataSource,
    ...productMetadataSource,
  };

  const mergedLimitedEdition = mergeLimitedEditionSections(
    metadataSource,
    productMetadataSource,
    artworkData,
  );

  if (Object.keys(mergedLimitedEdition).length > 0) {
    productMetadata.limitedEdition = {
      ...(productMetadata?.limitedEdition ?? {}),
      ...mergedLimitedEdition,
    };
    productMetadata.limited_edition = {
      ...(productMetadata?.limited_edition ?? {}),
      ...mergedLimitedEdition,
    };
  }

  const candidateSources = [
    productMetadata,
    productMetadata?.edition,
    productMetadata?.limitedEdition,
    productMetadata?.limited_edition,
    artworkData,
    artworkData?.edition,
    metadataSource,
  ];

  const editionNumberFallback = pickFirstCandidateFromSources(candidateSources, NUMBER_CANDIDATES);
  const editionSizeFallback = pickFirstCandidateFromSources(candidateSources, SIZE_CANDIDATES);

  const limitedEditionCombined = {
    ...(productMetadata?.limitedEdition ?? {}),
  };
  if (limitedEditionCombined.number === undefined && editionNumberFallback !== null) {
    limitedEditionCombined.number = editionNumberFallback;
  }
  if (limitedEditionCombined.size === undefined && editionSizeFallback !== null) {
    limitedEditionCombined.size = editionSizeFallback;
  }
  if (limitedEditionCombined.edition_size === undefined && editionSizeFallback !== null) {
    limitedEditionCombined.edition_size = editionSizeFallback;
  }

  const limitedEditionSnakeCombined = {
    ...(productMetadata?.limited_edition ?? limitedEditionCombined),
  };
  if (limitedEditionSnakeCombined.number === undefined && limitedEditionCombined.number !== undefined) {
    limitedEditionSnakeCombined.number = limitedEditionCombined.number;
  }
  if (limitedEditionSnakeCombined.size === undefined && limitedEditionCombined.size !== undefined) {
    limitedEditionSnakeCombined.size = limitedEditionCombined.size;
  }
  if (limitedEditionSnakeCombined.edition_size === undefined && limitedEditionCombined.edition_size !== undefined) {
    limitedEditionSnakeCombined.edition_size = limitedEditionCombined.edition_size;
  }

  return {
    ...productMetadata,
    edition_number: productMetadata?.edition_number ?? editionNumberFallback ?? undefined,
    edition_size: productMetadata?.edition_size ?? editionSizeFallback ?? undefined,
    edition: {
      ...(productMetadata?.edition ?? {}),
      number: productMetadata?.edition?.number ?? editionNumberFallback ?? undefined,
      size: productMetadata?.edition?.size ?? editionSizeFallback ?? undefined,
    },
    limitedEdition: limitedEditionCombined,
    limited_edition: limitedEditionSnakeCombined,
  };
}

export function resolveLimitedEditionContext(artworkData = {}) {
  const metadata = buildLimitedEditionMetadata(artworkData);
  const editionNumber = resolveEditionNumber(metadata);
  const editionSize = resolveEditionSize(metadata);
  const productHoldingType =
    artworkData?.holding_type
    ?? metadata?.holding_type
    ?? artworkData?.product_holding_type
    ?? metadata?.product_holding_type
    ?? null;

  const isLimitedEdition =
    metadata?.is_limited_edition === true
    || productHoldingType === 'limited_edition'
    || isPositiveInteger(editionNumber)
    || isPositiveInteger(editionSize);

  return {
    metadata,
    editionNumber,
    editionSize,
    productHoldingType,
    isLimitedEdition,
  };
}

export function resolveLimitedEditionSizeData(artworkData = {}, options = {}) {
  const metadata = options.metadata ?? buildLimitedEditionMetadata(artworkData);
  const candidateSources = [
    options.metadata ?? null,
    metadata,
    metadata?.limitedEdition,
    artworkData,
    artworkData?.product_metadata,
  ].filter(Boolean);

  const sizeKeyRaw = options.sizeKey
    ?? pickFirstCandidateFromSources(candidateSources, SIZE_KEY_CANDIDATES);
  const normalizedSizeKey = typeof sizeKeyRaw === 'string'
    ? sizeKeyRaw.toLowerCase()
    : null;

  const editionOptions = artworkData?.artwork_limited_editions ?? {};

  let matchedSizeEntry = null;
  if (normalizedSizeKey && editionOptions && typeof editionOptions === 'object') {
    const direct =
      editionOptions[sizeKeyRaw]
      ?? editionOptions[normalizedSizeKey]
      ?? editionOptions[sizeKeyRaw?.toUpperCase?.()];

    if (direct) {
      matchedSizeEntry = direct;
    } else {
      const editionValues = Object.values(editionOptions);
      for (let index = 0; index < editionValues.length; index += 1) {
        const entry = editionValues[index];
        if (!entry || typeof entry !== 'object') {
          continue;
        }
        const entryKey = (
          entry.size
          ?? entry.key
          ?? entry.id
          ?? entry.name
          ?? null
        );
        if (typeof entryKey !== 'string') {
          continue;
        }
        const normalizedEntryKey = entryKey.toLowerCase();
        if (normalizedEntryKey === normalizedSizeKey) {
          matchedSizeEntry = entry;
          break;
        }
      }
    }
  }

  if (!matchedSizeEntry) {
    const sizeDataCandidate = pickFirstCandidateFromSources(candidateSources, SIZE_DATA_CANDIDATES);
    if (sizeDataCandidate && typeof sizeDataCandidate === 'object') {
      matchedSizeEntry = sizeDataCandidate;
    }
  }

  return {
    sizeKey: normalizedSizeKey ?? (typeof sizeKeyRaw === 'string' ? sizeKeyRaw : null),
    data: matchedSizeEntry ?? null,
  };
}

export default {
  resolveEditionNumber,
  resolveEditionSize,
  buildLimitedEditionMetadata,
  resolveLimitedEditionContext,
  resolveLimitedEditionSizeData,
};
